import { zodResolver } from "@hookform/resolvers/zod";
// import { t } from "@lingui/macro";
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  Code as CodeIcon,
  CodeBlock as CodeBlockIcon,
  HighlighterCircle,
  Image as ImageIcon,
  KeyReturn,
  LinkSimple,
  ListBullets,
  ListNumbers,
  Minus,
  Paragraph as ParagraphIcon,
  TextAlignCenter,
  TextAlignJustify,
  TextAlignLeft,
  TextAlignRight,
  TextAUnderline,
  TextB,
  TextHOne,
  TextHThree,
  TextHTwo,
  TextIndent,
  TextItalic,
  TextOutdent,
  TextStrikethrough,
} from "@phosphor-icons/react";
import { PopoverTrigger } from "@radix-ui/react-popover";
import { cn } from "@stylingcv/utils";
import { Highlight } from "@tiptap/extension-highlight";
import { Image } from "@tiptap/extension-image";
import { Link } from "@tiptap/extension-link";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import type { Editor, EditorContentProps } from "@tiptap/react";
import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { forwardRef, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "./button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "./form";
import { Input } from "./input";
import { Popover, PopoverContent } from "./popover";
import { Skeleton } from "./skeleton";
import { Toggle } from "./toggle";
import { Tooltip } from "./tooltip";

const InsertImageFormSchema = z.object({
  src: z.string(),
  alt: z.string().optional(),
});

type InsertImageFormValues = z.infer<typeof InsertImageFormSchema>;

type InsertImageProps = {
  onInsert: (value: InsertImageFormValues) => void;
};

const InsertImageForm = ({ onInsert }: InsertImageProps) => {
  const form = useForm<InsertImageFormValues>({
    resolver: zodResolver(InsertImageFormSchema),
    defaultValues: { src: "", alt: "" },
  });

  const onSubmit = (values: InsertImageFormValues) => {
    onInsert(values);
    form.reset();
  };

  return (
    <Form {...form}>
      <form
        className="space-y-3"
        onSubmit={async (event) => {
          event.stopPropagation();
          event.preventDefault();

          await form.handleSubmit(onSubmit)();
        }}
      >
        <p className="prose prose-sm prose-zinc dark:prose-invert">
          Insert an image from an external URL and use it on your resume.
        </p>

        <FormField
          name="src"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL</FormLabel>
              <FormControl>
                <Input placeholder="https://..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="alt"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="!mt-5 ml-auto max-w-fit">
          <Button type="submit" variant="secondary" size="sm">
            Insert Image
          </Button>
        </div>
      </form>
    </Form>
  );
};

const tbBtn =
  "size-[26px] shrink-0 rounded-[5px] p-0 text-[#404040] hover:bg-white hover:text-[#268599] data-[state=on]:bg-[#268599] data-[state=on]:text-white";
const TbSep = () => <span className="mx-1 h-5 w-px shrink-0 bg-gray-200" />;

const Toolbar = ({ editor }: { editor: Editor }) => {
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-[#E5E5E5] bg-[#FAFAFA] p-1.5">
      {/* Text formatting */}
      <Tooltip content="Bold">
        <Toggle
          type="button"
          pressed={editor.isActive("bold")}
          disabled={!editor.can().chain().toggleBold().run()}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
          className={tbBtn}
        >
          <TextB size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Italic">
        <Toggle
          type="button"
          pressed={editor.isActive("italic")}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
          className={tbBtn}
        >
          <TextItalic size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Strikethrough">
        <Toggle
          type="button"
          pressed={editor.isActive("strike")}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          onPressedChange={() => editor.chain().focus().toggleStrike().run()}
          className={tbBtn}
        >
          <TextStrikethrough size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Underline">
        <Toggle
          type="button"
          pressed={editor.isActive("underline")}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
          className={tbBtn}
        >
          <TextAUnderline size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Highlight">
        <Toggle
          type="button"
          pressed={editor.isActive("highlight")}
          disabled={!editor.can().chain().focus().toggleHighlight().run()}
          onPressedChange={() => editor.chain().focus().toggleHighlight().run()}
          className={tbBtn}
        >
          <HighlighterCircle size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Hyperlink">
        <Button type="button" variant="ghost" onClick={setLink} className={tbBtn}>
          <LinkSimple size={14} />
        </Button>
      </Tooltip>

      <TbSep />

      {/* Code */}
      <Tooltip content="Inline Code">
        <Toggle
          type="button"
          pressed={editor.isActive("code")}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          onPressedChange={() => editor.chain().focus().toggleCode().run()}
          className={tbBtn}
        >
          <CodeIcon size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Code Block">
        <Toggle
          type="button"
          pressed={editor.isActive("codeBlock")}
          disabled={!editor.can().chain().focus().toggleCodeBlock().run()}
          onPressedChange={() => editor.chain().focus().toggleCodeBlock().run()}
          className={tbBtn}
        >
          <CodeBlockIcon size={14} />
        </Toggle>
      </Tooltip>

      <TbSep />

      {/* Headings */}
      <Tooltip content="Heading 1">
        <Toggle
          type="button"
          pressed={editor.isActive("heading", { level: 1 })}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 1 }).run()}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={tbBtn}
        >
          <TextHOne size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Heading 2">
        <Toggle
          type="button"
          pressed={editor.isActive("heading", { level: 2 })}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 2 }).run()}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={tbBtn}
        >
          <TextHTwo size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Heading 3">
        <Toggle
          type="button"
          pressed={editor.isActive("heading", { level: 3 })}
          disabled={!editor.can().chain().focus().toggleHeading({ level: 3 }).run()}
          onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={tbBtn}
        >
          <TextHThree size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Paragraph">
        <Toggle
          type="button"
          pressed={editor.isActive("paragraph")}
          onPressedChange={() => editor.chain().focus().setParagraph().run()}
          className={tbBtn}
        >
          <ParagraphIcon size={14} />
        </Toggle>
      </Tooltip>

      <TbSep />

      {/* Alignment */}
      <Tooltip content="Align Left">
        <Toggle
          type="button"
          pressed={editor.isActive({ textAlign: "left" })}
          disabled={!editor.can().chain().focus().setTextAlign("left").run()}
          onPressedChange={() => editor.chain().focus().setTextAlign("left").run()}
          className={tbBtn}
        >
          <TextAlignLeft size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Align Center">
        <Toggle
          type="button"
          pressed={editor.isActive({ textAlign: "center" })}
          disabled={!editor.can().chain().focus().setTextAlign("center").run()}
          onPressedChange={() => editor.chain().focus().setTextAlign("center").run()}
          className={tbBtn}
        >
          <TextAlignCenter size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Align Right">
        <Toggle
          type="button"
          pressed={editor.isActive({ textAlign: "right" })}
          disabled={!editor.can().chain().focus().setTextAlign("right").run()}
          onPressedChange={() => editor.chain().focus().setTextAlign("right").run()}
          className={tbBtn}
        >
          <TextAlignRight size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Align Justify">
        <Toggle
          type="button"
          pressed={editor.isActive({ textAlign: "justify" })}
          disabled={!editor.can().chain().focus().setTextAlign("justify").run()}
          onPressedChange={() => editor.chain().focus().setTextAlign("justify").run()}
          className={tbBtn}
        >
          <TextAlignJustify size={14} />
        </Toggle>
      </Tooltip>

      <TbSep />

      {/* Lists */}
      <Tooltip content="Bullet List">
        <Toggle
          type="button"
          pressed={editor.isActive("bulletList")}
          disabled={!editor.can().chain().focus().toggleBulletList().run()}
          onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
          className={tbBtn}
        >
          <ListBullets size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Numbered List">
        <Toggle
          type="button"
          pressed={editor.isActive("orderedList")}
          disabled={!editor.can().chain().focus().toggleOrderedList().run()}
          onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
          className={tbBtn}
        >
          <ListNumbers size={14} />
        </Toggle>
      </Tooltip>

      <Tooltip content="Outdent">
        <Button
          type="button"
          variant="ghost"
          disabled={!editor.can().chain().focus().liftListItem("listItem").run()}
          onClick={() => editor.chain().focus().liftListItem("listItem").run()}
          className={tbBtn}
        >
          <TextOutdent size={14} />
        </Button>
      </Tooltip>

      <Tooltip content="Indent">
        <Button
          type="button"
          variant="ghost"
          disabled={!editor.can().chain().focus().sinkListItem("listItem").run()}
          onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
          className={tbBtn}
        >
          <TextIndent size={14} />
        </Button>
      </Tooltip>

      <TbSep />

      {/* Insert */}
      <Popover>
        <Tooltip content="Insert Image">
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" className={tbBtn}>
              <ImageIcon size={14} />
            </Button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent className="w-80">
          <InsertImageForm onInsert={(props) => editor.chain().focus().setImage(props).run()} />
        </PopoverContent>
      </Popover>

      <Tooltip content="Insert Break Line">
        <Button
          type="button"
          variant="ghost"
          disabled={!editor.can().chain().focus().setHardBreak().run()}
          onClick={() => editor.chain().focus().setHardBreak().run()}
          className={tbBtn}
        >
          <KeyReturn size={14} />
        </Button>
      </Tooltip>

      <Tooltip content="Insert Horizontal Rule">
        <Button
          type="button"
          variant="ghost"
          disabled={!editor.can().chain().focus().setHorizontalRule().run()}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className={tbBtn}
        >
          <Minus size={14} />
        </Button>
      </Tooltip>

      <TbSep />

      {/* History */}
      <Tooltip content="Undo">
        <Button
          type="button"
          variant="ghost"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
          className={tbBtn}
        >
          <ArrowCounterClockwise size={14} />
        </Button>
      </Tooltip>

      <Tooltip content="Redo">
        <Button
          type="button"
          variant="ghost"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
          className={tbBtn}
        >
          <ArrowClockwise size={14} />
        </Button>
      </Tooltip>
    </div>
  );
};

type RichInputProps = {
  content?: string;
  onChange?: (value: string) => void;
  hideToolbar?: boolean;
  className?: string;
  editorClassName?: string;
  footer?: (editor: Editor) => React.ReactNode;
} & Omit<EditorContentProps, "ref" | "editor" | "content" | "value" | "onChange" | "className">;

export const RichInput = forwardRef<Editor, RichInputProps>(
  (
    { content, onChange, footer, hideToolbar = false, className, editorClassName, ...props },
    _ref,
  ) => {
    const editor = useEditor({
      extensions: [
        StarterKit,
        Image,
        Underline,
        Highlight,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Link.extend({ inclusive: false }).configure({ openOnClick: false }),
      ],
      editorProps: {
        attributes: {
          class: cn(
            "prose prose-sm prose-zinc min-h-[150px] max-h-[350px] max-w-none overflow-y-auto dark:prose-invert focus:outline-none [&_*]:my-2",
            editorClassName,
          ),
        },
      },
      content,
      parseOptions: { preserveWhitespace: "full" },
      onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    });

    // Sync externally-set content (e.g. AI generation) into the editor.
    // Skip when the editor is focused — that means the user is typing and the
    // editor already owns its own state; calling setContent would reset the cursor.
    useEffect(() => {
      if (!editor || editor.isFocused) return;
      const incoming = content ?? "";
      if (editor.getHTML() !== incoming) {
        editor.commands.setContent(incoming, false);
      }
    }, [content, editor]);

    if (!editor) {
      return (
        <div className={cn("overflow-hidden rounded-xl border border-[#E5E5E5]", className)}>
          <Skeleton className={cn("h-[42px] w-full rounded-none", hideToolbar && "hidden")} />
          <Skeleton className="h-[90px] w-full rounded-none" />
        </div>
      );
    }

    return (
      <div
        className={cn("overflow-hidden rounded-xl border border-[#E5E5E5] cursor-text", className)}
        onClick={() => editor.commands.focus()}
      >
        {!hideToolbar && <Toolbar editor={editor} />}

        <EditorContent
          editor={editor}
          className="min-h-[160px] w-full px-3 py-3 focus-visible:outline-none"
          {...props}
        />

        {footer?.(editor)}
      </div>
    );
  },
);

RichInput.displayName = "RichInput";
